import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useLanguage } from '../context/LanguageContext';
import { Zap, Target, TrendingUp, ChevronRight, Timer, FileText, Bike, Bot, Activity, Instagram } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-gym-guide/artifacts/gwvje8fa_IMG_20220211_145748_413%20%285%29.jpg";
const INSTAGRAM_URL = "https://www.instagram.com/process_body_pt/";

const FEATURE_ICONS = [
  <Zap className="w-8 h-8" />,
  <Bot className="w-8 h-8" />,
  <Bike className="w-8 h-8" />,
  <Timer className="w-8 h-8" />,
  <FileText className="w-8 h-8" />,
  <Target className="w-8 h-8" />,
  <TrendingUp className="w-8 h-8" />,
  <Activity className="w-8 h-8" />,
  <ChevronRight className="w-8 h-8" />
];

const LandingPage = () => {
  const { landing: L } = useLanguage();

  return (
    <div className="min-h-screen bg-[#09090b] overflow-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src={LOGO_URL} alt="Process Body" className="w-9 h-9 sm:w-12 sm:h-12 rounded-full object-cover flex-shrink-0" />
            <span className="hidden sm:inline text-lg sm:text-2xl font-bold tracking-tight truncate" style={{ fontFamily: 'Barlow Condensed' }}>
              PROCESS<span className="text-[#32CD32]">BODY</span>
            </span>
          </Link>
          <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
            <LanguageSwitcher variant="nav" />
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:text-[#32CD32] font-medium uppercase tracking-wider text-xs sm:text-sm px-2 sm:px-4" data-testid="login-nav-btn">
                {L.navLogin}
              </Button>
            </Link>
            <Link to="/register">
              <Button className="btn-primary px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm" data-testid="register-nav-btn">
                {L.navStart}
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20">
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1758875569612-94d5e0f1a35f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxneW0lMjB3b3Jrb3V0JTIwZml0bmVzcyUyMHRyYWluaW5nfGVufDB8fHx8MTc3MzA5MTMwMXww&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3)'
          }}
        />
        
        {/* Grid overlay */}
        <div className="absolute inset-0 grid-bg z-10 opacity-50" />
        
        {/* Glow effect */}
        <div className="absolute inset-0 hero-glow z-10" />

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 py-20 text-center">
          <p className="text-xs font-mono tracking-[0.2em] sm:tracking-[0.3em] text-[#32CD32] mb-6 opacity-0 animate-fadeInUp uppercase">
            {L.heroTag}
          </p>
          
          <h1 
            className="text-5xl sm:text-6xl md:text-8xl font-black tracking-tighter uppercase mb-8 opacity-0 animate-fadeInUp stagger-1"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            {L.heroTitle}
            <br />
            <span className="text-[#32CD32] glow-text">{L.heroTitleHighlight}</span>
          </h1>
          
          <p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-10 sm:mb-12 opacity-0 animate-fadeInUp stagger-2">
            {L.heroSubtitle}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center opacity-0 animate-fadeInUp stagger-3">
            <Link to="/register" className="w-full sm:w-auto">
              <Button className="btn-primary w-full sm:w-auto px-8 sm:px-10 py-5 sm:py-6 rounded-full text-base sm:text-lg animate-pulse-glow" data-testid="hero-cta-btn">
                {L.heroCta}
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/login" className="w-full sm:w-auto">
              <Button variant="outline" className="w-full sm:w-auto px-8 sm:px-10 py-5 sm:py-6 rounded-full text-base sm:text-lg border-white/20 hover:border-[#32CD32] bg-transparent text-white hover:text-[#32CD32]" data-testid="hero-login-btn">
                {L.heroHasAccount}
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-[#32CD32] rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-xs font-mono tracking-[0.2em] sm:tracking-[0.3em] text-[#32CD32] mb-4 uppercase">
              {L.featTag}
            </p>
            <h2 
              className="text-3xl sm:text-4xl md:text-6xl font-bold tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              {L.featTitle} <span className="text-[#32CD32]">{L.featTitleHighlight}</span>
            </h2>
            <p className="text-gray-400 text-sm sm:text-base mt-4 max-w-2xl mx-auto">
              {L.featSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {L.features.map((feature, index) => (
              <div 
                key={index}
                className="group bg-[#121212] border border-white/10 rounded-xl p-6 sm:p-8 card-hover"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#32CD32]/10 flex items-center justify-center mb-4 sm:mb-6 text-[#32CD32] group-hover:bg-[#32CD32] group-hover:text-black transition-colors duration-300">
                  {FEATURE_ICONS[index]}
                </div>
                <h3 
                  className="text-lg sm:text-xl font-bold mb-3 uppercase tracking-tight"
                  style={{ fontFamily: 'Barlow Condensed' }}
                >
                  {feature.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quem Somos Section */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6 bg-black/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 items-center">
            {/* Image */}
            <div className="relative">
              <div 
                className="aspect-square rounded-3xl overflow-hidden"
                style={{
                  backgroundImage: 'url(https://images.unsplash.com/photo-1766287453739-c3ffc3f37d05?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxneW0lMjB3b3Jrb3V0JTIwZml0bmVzcyUyMHRyYWluaW5nfGVufDB8fHx8MTc3MzA5MTMwMXww&ixlib=rb-4.1.0&q=85)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
              <div className="absolute -bottom-4 right-2 sm:-bottom-6 sm:-right-6 w-24 h-24 sm:w-32 sm:h-32 bg-[#32CD32] rounded-2xl flex items-center justify-center">
                <div className="text-center text-black px-2">
                  <p className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>5+</p>
                  <p className="text-xs font-medium uppercase">{L.yearsExp}</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div>
              <p className="text-xs font-mono tracking-[0.2em] sm:tracking-[0.3em] text-[#32CD32] mb-4 uppercase mt-8 lg:mt-0">
                {L.aboutTag}
              </p>
              <h2 
                className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight uppercase mb-6"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                {L.aboutTitle} <span className="text-[#32CD32]">{L.aboutTitleHighlight}</span>
              </h2>
              <p className="text-sm sm:text-base text-gray-400 mb-6 leading-relaxed">
                {L.aboutP1}
              </p>
              <p className="text-sm sm:text-base text-gray-400 mb-8 leading-relaxed">
                {L.aboutP2}
              </p>
              
              <div className="grid grid-cols-3 gap-3 sm:gap-6 mb-8">
                <div className="text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-[#32CD32]" style={{ fontFamily: 'Barlow Condensed' }}>1000+</p>
                  <p className="text-[10px] sm:text-xs text-gray-500 uppercase">{L.statUsers}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-[#32CD32]" style={{ fontFamily: 'Barlow Condensed' }}>3000+</p>
                  <p className="text-[10px] sm:text-xs text-gray-500 uppercase">{L.statPlans}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-[#32CD32]" style={{ fontFamily: 'Barlow Condensed' }}>95%</p>
                  <p className="text-[10px] sm:text-xs text-gray-500 uppercase">{L.statSatisfaction}</p>
                </div>
              </div>

              {/* Instagram Link */}
              <a 
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-full text-white font-medium hover:opacity-90 transition-opacity cursor-pointer"
                data-testid="instagram-link"
              >
                <Instagram className="w-5 h-5" />
                <span>{L.instagramFollow}</span>
              </a>
              <p className="text-xs text-gray-500 mt-2">instagram.com/process_body_pt</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6">
        <div 
          className="max-w-5xl mx-auto rounded-3xl overflow-hidden relative"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/8060691/pexels-photo-8060691.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative z-10 p-8 sm:p-12 md:p-20 text-center">
            <h2 
              className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight uppercase mb-6"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              {L.ctaTitle} <span className="text-[#32CD32]">{L.ctaTitleHighlight}</span>?
            </h2>
            <p className="text-base sm:text-lg text-gray-300 mb-8 sm:mb-10 max-w-xl mx-auto">
              {L.ctaSubtitle}
            </p>
            <Link to="/register">
              <Button className="btn-primary px-8 sm:px-12 py-5 sm:py-6 rounded-full text-base sm:text-lg" data-testid="cta-register-btn">
                {L.ctaBtn}
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-10 sm:py-12 px-4 sm:px-6 pb-24 sm:pb-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Process Body" className="w-10 h-10 rounded-full object-cover" />
            <span className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              PROCESS<span className="text-[#32CD32]">BODY</span>
            </span>
          </div>
          
          {/* Social Links */}
          <div className="flex items-center gap-4">
            <a 
              href="https://www.instagram.com/process_body_pt/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-gradient-to-r hover:from-purple-500 hover:via-pink-500 hover:to-orange-500 flex items-center justify-center transition-all cursor-pointer"
              data-testid="footer-instagram-link"
              title="Instagram @process_body_pt"
            >
              <Instagram className="w-5 h-5" />
            </a>
          </div>
          
          <p className="text-sm text-gray-500">
            © 2024 Process Body. {L.footerTagline}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
